/**
 * Recurrence patterns for todo tasks
 */

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number; // Every N [frequency] (e.g., every 2 weeks)
  daysOfWeek?: number[]; // 0=Sunday through 6=Saturday (for weekly)
  dayOfMonth?: number; // 1-31 (for monthly)
  endDate?: Date | null; // Optional end date
  count?: number | null; // Optional max occurrences
}

// Pre-defined recurrence patterns for quick selection
export const RECURRENCE_PRESETS: Array<{
  label: string;
  value: string;
  rule: RecurrenceRule;
}> = [
  {
    label: 'Daily',
    value: 'daily',
    rule: { frequency: 'daily', interval: 1 },
  },
  {
    label: 'Every weekday',
    value: 'weekdays',
    rule: { frequency: 'weekly', interval: 1, daysOfWeek: [1, 2, 3, 4, 5] },
  },
  {
    label: 'Weekly',
    value: 'weekly',
    rule: { frequency: 'weekly', interval: 1 },
  },
  {
    label: 'Every 2 weeks',
    value: 'biweekly',
    rule: { frequency: 'weekly', interval: 2 },
  },
  {
    label: 'Monthly',
    value: 'monthly',
    rule: { frequency: 'monthly', interval: 1 },
  },
  {
    label: 'Yearly',
    value: 'yearly',
    rule: { frequency: 'yearly', interval: 1 },
  },
];

/**
 * Format a recurrence rule for display
 */
export const formatRecurrence = (rule: RecurrenceRule): string => {
  const { frequency, interval, daysOfWeek } = rule;

  // Handle weekday-only pattern
  if (frequency === 'weekly' && daysOfWeek?.length === 5) {
    const weekdays = [1, 2, 3, 4, 5];
    if (daysOfWeek.every((d) => weekdays.includes(d))) {
      return 'Every weekday';
    }
  }

  // Handle specific days of week
  if (frequency === 'weekly' && daysOfWeek && daysOfWeek.length > 0) {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const days = daysOfWeek.map((d) => dayNames[d]).join(', ');
    if (interval === 1) {
      return `Every ${days}`;
    }
    return `Every ${interval} weeks on ${days}`;
  }

  // Simple patterns
  if (interval === 1) {
    switch (frequency) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      case 'yearly':
        return 'Yearly';
    }
  }

  // Interval patterns
  const frequencyWords = {
    daily: 'days',
    weekly: 'weeks',
    monthly: 'months',
    yearly: 'years',
  };

  return `Every ${interval} ${frequencyWords[frequency]}`;
};

/**
 * Parse a recurrence pattern from natural language (basic patterns)
 */
export const parseRecurrenceFromText = (text: string): RecurrenceRule | null => {
  const lowerText = text.toLowerCase();

  // Daily patterns
  if (/\b(every\s+day|daily)\b/.test(lowerText)) {
    return { frequency: 'daily', interval: 1 };
  }

  // Every N days
  const daysMatch = lowerText.match(/every\s+(\d+)\s+days?/);
  if (daysMatch) {
    return { frequency: 'daily', interval: parseInt(daysMatch[1]) };
  }

  // Weekday patterns
  if (/\b(every\s+weekday|weekdays)\b/.test(lowerText)) {
    return { frequency: 'weekly', interval: 1, daysOfWeek: [1, 2, 3, 4, 5] };
  }

  // Specific day patterns (every Monday, every Tuesday, etc.)
  const dayMap: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  for (const [dayName, dayNum] of Object.entries(dayMap)) {
    const dayRegex = new RegExp(`\\bevery\\s+${dayName}\\b`, 'i');
    if (dayRegex.test(lowerText)) {
      return { frequency: 'weekly', interval: 1, daysOfWeek: [dayNum] };
    }
  }

  // Weekly patterns
  if (/\b(every\s+week|weekly)\b/.test(lowerText)) {
    return { frequency: 'weekly', interval: 1 };
  }

  // Every N weeks
  const weeksMatch = lowerText.match(/every\s+(\d+)\s+weeks?/);
  if (weeksMatch) {
    return { frequency: 'weekly', interval: parseInt(weeksMatch[1]) };
  }

  // Biweekly
  if (/\bbiweekly\b/.test(lowerText)) {
    return { frequency: 'weekly', interval: 2 };
  }

  // Monthly patterns
  if (/\b(every\s+month|monthly)\b/.test(lowerText)) {
    return { frequency: 'monthly', interval: 1 };
  }

  // Every N months
  const monthsMatch = lowerText.match(/every\s+(\d+)\s+months?/);
  if (monthsMatch) {
    return { frequency: 'monthly', interval: parseInt(monthsMatch[1]) };
  }

  // Yearly patterns
  if (/\b(every\s+year|yearly|annually)\b/.test(lowerText)) {
    return { frequency: 'yearly', interval: 1 };
  }

  return null;
};

// Alias for formatRecurrence
export const getRecurrenceDescription = formatRecurrence;
